// Inline citation chips for the /threads chat lane.
//
// COPY-ADAPTED from the graph chat panel (Center/Graph/gs/components/
// GraphChatPanel.jsx §37.5) per NEW_CHAT_PAGE_PLAN.md — this page imports
// NOTHING from the graph chat files and edits none of them. What is copied is
// the mechanism, verbatim in behaviour: the model emits `[n]`; the render maps
// a marker to the server-derived SOURCE NAME, grouping same-source runs ×k,
// and the numbers live in the tooltip and in copied markdown.
//
// The one adaptation: the chat lane has no `action` events (plan §Phase A.3),
// so nothing here reads them — but `stripActionResidue` is kept as a
// defence-in-depth belt anyway, because the cost is one regex and the failure
// it prevents is a JSON blob rendered to the user as prose.
import { useRef, useState } from "react";

import { getMapDocPreview } from "../../../api/mapApi";

// Render-side belt: if action JSON ever leaks past a server splitter, strip it
// here rather than show it.
//
// TOOL-SYNTAX PATTERNS (plan Part 6): the answer model is tool-trained, and the
// probe caught it LEAKING tool syntax into the text path when tools were
// withheld. The server strips it; this is the second belt, because the failure
// it prevents is a ChatML control token or a raw function-call blob rendered to
// the user as prose. Defensive only — on a healthy stream none of these match.
export const stripActionResidue = (text) =>
  String(text || "")
    .replace(/```[ \t]*(?:json)?[ \t]*\n?\{\s*"actions"[\s\S]*?```/gi, "")
    .replace(/\{\s*"actions"\s*:\s*\[[\s\S]*?\]\s*\}/g, "")
    // Hermes/Qwen tool blocks, closed form.
    .replace(/<tool_call>[\s\S]*?<\/tool_call>/gi, "")
    .replace(/<tool_response>[\s\S]*?<\/tool_response>/gi, "")
    // ...and the bare/unpaired tags plus the ChatML special-token spellings
    // (`<|tool_call|>`, `<|im_start|>`, …), which arrive alone when a leak is
    // cut short mid-stream.
    .replace(/<\/?tool_(?:call|response)>/gi, "")
    .replace(/<\|[^|>]*\|>/g, "")
    // A fenced or bare native call object. `arguments` is flat for every tool
    // in the five-tool box (string/int fields only — search_web{query},
    // read_url{url}, search_corpus{query,date_from,date_to},
    // read_citation{citation}, entity_lookup{name}), so the lazy inner `}` is
    // the right stop.
    .replace(
      /```[ \t]*(?:json)?[ \t]*\n?\{\s*"name"\s*:\s*"[\w.-]+"\s*,\s*"arguments"[\s\S]*?```/gi,
      "",
    )
    .replace(
      /\{\s*"name"\s*:\s*"[\w.-]+"\s*,\s*"arguments"\s*:\s*\{[\s\S]*?\}\s*\}/g,
      "",
    );

// [n] → markdown links [n](#cite-n) so citation buttons render inline anywhere
// markdown flows; markers with no mapped doc are stripped (tolerant parsing).
// Runs of adjacent markers group by SOURCE — one chip per distinct source in
// the run. Drift tolerance for the comma variant the model also emits
// ([12,13,14] — one bracket, comma-joined): a marker is \d+(,\d+)* and every
// int inside the run is collected.
export const linkifyCitations = (text, citedNs, byN) =>
  stripActionResidue(text).replace(
    /(\[\d+(?:\s*,\s*\d+)*\](?:[ \t]*\[\d+(?:\s*,\s*\d+)*\])*)/g,
    (run) => {
      const ns = [...new Set(
        [...run.matchAll(/\d+/g)].map((m) => Number(m[0])),
      )].filter((n) => citedNs.has(n));
      if (!ns.length) return "";
      const groups = [];
      const bySrc = new Map();
      for (const n of ns) {
        const src = byN.get(n)?.source || `#${n}`;
        if (bySrc.has(src)) bySrc.get(src).push(n);
        else {
          const g = [n];
          bySrc.set(src, g);
          groups.push(g);
        }
      }
      return groups.map((g) => `[${g.join(",")}](#cite-${g.join("-")})`).join(" ");
    },
  );

// docId -> {screenshot} | null. Module-level and session-lived on purpose: the
// preview is a just-in-time fetch behind an intentional hover, and a second
// hover on the same source must not pay for it again.
const previewCache = new Map();

export const CitationButton = ({ ns, docs, onOpen }) => {
  // Chip label = the server-derived source name; numbered fallback covers a
  // not-yet-redeployed server or an old peer. ×k when one chip absorbed a
  // same-source run; the numbers live in the tooltip.
  const doc = docs[0];
  const [hover, setHover] = useState(false);
  const [preview, setPreview] = useState(previewCache.get(doc.documentId));
  const timerRef = useRef(null);
  const isPeer = doc.origin === "peer";
  // Web-search results (plan Part 6). They ARE local docs — persisted to the
  // corpus with real document_ids — so everything else about them (chip click,
  // slide-over, preview fetch) is the ordinary path; only the accent differs.
  const isWeb = doc.origin === "web";
  const label = doc.source || `#${ns[0]}`;
  const numbers = `[${ns.join("][")}]`;

  const onEnter = () => {
    setHover(true);
    // Peer docs have no local screenshot — title-only hover card.
    //
    // Web docs DO get the fetch attempted: screenshots backfill asynchronously
    // (the web_enrich sweep), so the preview is simply absent until it lands.
    // Failure is silent by construction — an error caches null, an empty
    // payload renders no <img>, and either way the hover card is the title
    // alone. Nothing here can produce a broken-image state.
    if (isPeer || previewCache.has(doc.documentId)) return;
    timerRef.current = setTimeout(() => {
      getMapDocPreview(doc.documentId)
        .then((res) => {
          previewCache.set(doc.documentId, res?.data || null);
          setPreview(res?.data || null);
        })
        .catch(() => previewCache.set(doc.documentId, null));
    }, 150); // JIT: only fetch when the hover is intentional
  };
  const onLeave = () => {
    setHover(false);
    clearTimeout(timerRef.current);
  };

  return (
    <span className="relative inline-block" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        type="button"
        onClick={() => onOpen(doc)}
        title={
          isPeer
            ? `${numbers} — federated source (peer server)`
            : isWeb
              ? `${numbers} — from the web (found by search)`
              : numbers
        }
        className={`mx-0.5 inline-flex h-4 max-w-44 items-center justify-center gap-0.5 truncate rounded px-1 align-text-top text-[10px] font-semibold transition ${
          isPeer
            ? "bg-amber-400/15 text-amber-400 ring-1 ring-amber-400/40 hover:bg-amber-400/25"
            : isWeb
              ? "bg-emerald-400/15 text-emerald-400 ring-1 ring-emerald-400/40 hover:bg-emerald-400/25"
              : "bg-velBlue/15 text-velBlue hover:bg-velBlue/30"
        }`}
      >
        <span className="truncate">{label}</span>
        {docs.length > 1 && (
          <span className="shrink-0 opacity-70">×{docs.length}</span>
        )}
      </button>
      {hover && (
        <span className="absolute bottom-full left-1/2 z-40 mb-1.5 w-52 -translate-x-1/2 overflow-hidden rounded-lg border border-mainDivider bg-mainBg shadow-xl">
          {preview?.screenshot && (
            <img
              src={preview.screenshot}
              alt=""
              draggable={false}
              // A presign that has expired (or an enrichment shot that vanished)
              // must collapse to the title-only card, never to a broken-image
              // glyph inside the hover popover. The node is remounted on every
              // hover, so hiding it in place is enough and costs no state.
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="h-24 w-full object-cover object-top"
            />
          )}
          <span className="block px-2 py-1.5 text-[10px] leading-tight text-mainText">
            {doc.title}
          </span>
        </span>
      )}
    </span>
  );
};

// The markdown `components` map for one answer: `#cite-n` hrefs are intercepted
// and rendered as chips, every other link stays an ordinary external link.
// Built per entry (the doc map is per answer — an `n` means a different
// document in every answer).
export const citationComponents = (byN, onOpen) => ({
  a: ({ href, children }) => {
    const m = /^#cite-(\d+(?:-\d+)*)$/.exec(href || "");
    if (m) {
      const ns = m[1].split("-").map(Number);
      const docs = ns.map((n) => byN.get(n)).filter(Boolean);
      return docs.length ? (
        <CitationButton ns={ns} docs={docs} onOpen={onOpen} />
      ) : null;
    }
    return (
      <a href={href} target="_blank" rel="noreferrer" className="text-velBlue underline">
        {children}
      </a>
    );
  },
});
